import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Search, X, CornerDownLeft } from "lucide-react";
import topics from "@/data/topics.json";
import { searchTopics } from "@/lib/search";
import { lenisStore } from "@/lib/scroll";

const Highlight = ({ title, q }) => {
  const i = title.toLowerCase().indexOf(q.trim().toLowerCase());
  if (!q.trim() || i === -1) return title;
  const n = q.trim().length;
  return <>{title.slice(0, i)}<mark className="bg-lime text-ink rounded px-0.5">{title.slice(i, i + n)}</mark>{title.slice(i + n)}</>;
};

const Result = ({ r, q, active, onPick, onHover }) => (
  <li>
    <button
      data-testid={`search-result-${r.slug}`}
      onClick={() => onPick(r.slug)}
      onMouseEnter={onHover}
      className={`w-full text-left rounded-2xl px-4 py-4 border transition-colors duration-200 ${active ? "border-lime/60 bg-white/5" : "border-transparent"}`}
    >
      <span className="flex items-center justify-between gap-3">
        <span className="font-display font-bold text-lg text-white"><Highlight title={r.title} q={q} /></span>
        <span className="shrink-0 font-mono text-[12px] text-zinc-400">{r.count} {r.count === 1 ? "match" : "matches"}</span>
      </span>
      {r.snippet && (
        <span className="block mt-1.5 text-sm leading-relaxed text-zinc-300">
          {r.snippet.before}<mark className="bg-lime text-ink rounded px-0.5">{r.snippet.match}</mark>{r.snippet.after}
        </span>
      )}
    </button>
  </li>
);

export const SearchDialog = ({ open, onClose }) => {
  const [q, setQ] = useState("");
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const results = useMemo(() => searchTopics(q), [q]);

  useEffect(() => {
    if (!open) return undefined;
    lenisStore.current?.stop();
    const t = setTimeout(() => inputRef.current?.focus(), 50);
    return () => { clearTimeout(t); lenisStore.current?.start(); };
  }, [open]);

  useEffect(() => { setActive(0); }, [q]);

  const pick = (slug) => {
    onClose();
    setQ("");
    navigate(`/${slug}`);
  };

  const onKey = (e) => {
    if (e.key === "Escape") onClose();
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, results.length - 1)); }
    if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    if (e.key === "Enter" && results[active]) pick(results[active].slug);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="search-dialog"
          className="fixed inset-0 z-[80] flex items-start justify-center px-3 sm:px-6 pt-20 sm:pt-28"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onKeyDown={onKey}
        >
          <div className="absolute inset-0 bg-ink/80 backdrop-blur-md" onClick={onClose} data-testid="search-backdrop" />
          <motion.div
            initial={{ y: 30, scale: 0.97 }}
            animate={{ y: 0, scale: 1 }}
            exit={{ y: 20, scale: 0.98 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-2xl rounded-3xl border border-white/15 bg-panel shadow-[0_40px_120px_-30px_rgba(200,244,90,0.3)] overflow-hidden"
          >
            <div className="flex items-center gap-3 px-5 py-4 border-b border-white/10">
              <Search className="w-5 h-5 text-lime shrink-0" />
              <input
                ref={inputRef}
                data-testid="search-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={`Search ${topics.length} topics…`}
                className="flex-1 bg-transparent outline-none text-lg text-white placeholder:text-zinc-500 font-display"
              />
              <button data-testid="search-close-btn" onClick={onClose} className="p-1.5 rounded-full text-zinc-300 hover:text-ink hover:bg-lime transition-colors" aria-label="Close search">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div data-lenis-prevent className="max-h-[60vh] overflow-y-auto p-2">
              {q.trim().length < 2 ? (
                <p className="px-4 py-6 font-mono text-[12px] uppercase tracking-[0.2em] text-zinc-400">Type at least 2 letters to search titles and text</p>
              ) : results.length === 0 ? (
                <p data-testid="search-empty" className="px-4 py-6 text-zinc-300">No topics mention “{q.trim()}”.</p>
              ) : (
                <>
                  <p data-testid="search-count" className="px-4 pt-3 pb-1 font-mono text-[12px] uppercase tracking-[0.2em] text-zinc-400">
                    {results.length} {results.length === 1 ? "topic" : "topics"}
                  </p>
                  <ul>
                    {results.map((r, i) => (
                      <Result key={r.slug} r={r} q={q} active={i === active} onPick={pick} onHover={() => setActive(i)} />
                    ))}
                  </ul>
                </>
              )}
            </div>
            <div className="hidden sm:flex items-center gap-4 px-5 py-3 border-t border-white/10 font-mono text-[12px] text-zinc-400">
              <span>↑↓ to move</span>
              <span className="flex items-center gap-1"><CornerDownLeft className="w-3.5 h-3.5" /> to open</span>
              <span>esc to close</span>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
